import apiClient from "./v5Api";
import axios from "axios";
import { ENV } from "../configs/environment";
import { getSignoffRole } from "../utlis/actionPlanRoles";

/**
 * Dynamic Action Plan (v6) API.
 *
 * Reuses the shared `v5Api` axios instance (baseURL = ENV.API_URL) so it
 * inherits the same Bearer-token / Accept-Language request interceptor and the
 * 401/403 sign-out handling. Only the `/v6/...` paths differ.
 *
 * Contract: see
 * `components/userDetail/personDashboard/dynamicActionPlanV3/API.md` and
 * `ACTIONS_API.md`.
 */

const base = (seniorId) => `/v6/seniors/${encodeURIComponent(seniorId)}`;

// The backend accepts exactly these two authorization roles. Tenant-specific
// persona names are UI concerns and must never cross the service boundary.
const withApiRole = (body) =>
  body?.role ? { ...body, role: getSignoffRole(body.role) } : body;

// Shareable report slugs are deliberately public. Keep them on a separate
// client so an expired app session cannot add an Authorization header or
// trigger the authenticated client's global 401/403 sign-out interceptor.
const publicReportApi = axios.create({
  baseURL: ENV.API_URL,
  headers: { "Content-Type": "application/json" },
});

/** Persist an immutable, versioned action-plan report snapshot for 30 days. */
export const createShareableReport = async ({ seniorId, version, data }) => {
  const response = await publicReportApi.post("/v6/report", {
    seniorId,
    version,
    data,
  });
  return response.data;
};

/** Fetch a public report by its secret slug. Callers inspect 404/410 statuses. */
export const getShareableReport = async (slug) => {
  const response = await publicReportApi.get(
    `/v6/report/${encodeURIComponent(slug)}`,
  );
  return response.data;
};

/**
 * §1 Load action plan (initial render + persona switch).
 * `role` is optional — the backend resolves it from auth; it is only passed for
 * the demo/preview persona switch.
 */
export const getActionPlan = async (seniorId, role, tenant) => {
  const response = await apiClient.get(`${base(seniorId)}/action-plan`, {
    params: {
      ...(role ? { role: getSignoffRole(role) } : {}),
      ...(tenant ? { tenant } : {}),
    },
  });
  return response.data;
};

/** §3 / §4 Start or end a session (shared state-machine endpoint). */
export const patchActionPlanSession = async (seniorId, body) => {
  const response = await apiClient.patch(
    `${base(seniorId)}/action-plan/session`,
    withApiRole(body),
  );
  return response.data;
};

/**
 * §4.1 Record the current viewer's sign-off for the latest session.
 * Body requires signed, sessionId, and role (`caregiver` | `caremanager`);
 * optional visitNote/date are stored for that role. The response returns the
 * current session plus `signoffs.caregiver` and `signoffs.caremanager`.
 */
export const patchActionPlanSignoff = async (seniorId, body) => {
  const response = await apiClient.patch(
    `${base(seniorId)}/action-plan/signoff`,
    withApiRole(body),
  );
  return response.data;
};

/** §5 Toggle a checklist item (only while in session). */
export const patchChecklistItem = async (seniorId, itemId, body) => {
  const response = await apiClient.patch(
    `${base(seniorId)}/action-plan/checklist/${encodeURIComponent(itemId)}`,
    withApiRole(body),
  );
  return response.data;
};

/** §6 Confirm / reset the Care Manager "Next Step". */
export const patchNextStep = async (seniorId, body) => {
  const response = await apiClient.patch(
    `${base(seniorId)}/action-plan/next-step`,
    withApiRole(body),
  );
  return response.data;
};

/** §7 Share the visit/resident summary (also marks the Next Step done). */
export const shareSummary = async (seniorId, body) => {
  const response = await apiClient.post(
    `${base(seniorId)}/action-plan/summary/share`,
    withApiRole(body),
  );
  return response.data;
};

/**
 * Generic action confirm (action sheet without a `destination`).
 * `POST /v6/seniors/{id}/action-plan/actions/{actionId}`.
 */
export const postAction = async (seniorId, actionId, body) => {
  const response = await apiClient.post(
    `${base(seniorId)}/action-plan/actions/${encodeURIComponent(actionId)}`,
    withApiRole(body),
  );
  return response.data;
};

/** Add a referral for the senior. */
export const addReferral = async (seniorId, body) => {
  const response = await apiClient.post(`${base(seniorId)}/referrals`, body);
  return response.data;
};

/** §8 Add a note to the shared thread. */
export const addNote = async (seniorId, body) => {
  const response = await apiClient.post(
    `${base(seniorId)}/notes`,
    withApiRole(body),
  );
  return response.data;
};

/** P1 Senior Living daily roster for a facility. */
export const getRoster = async (facilityId) => {
  const response = await apiClient.get(
    `/v6/facilities/${encodeURIComponent(facilityId)}/roster`,
  );
  return response.data;
};

/** P4 Referral history for a senior (newest first). */
export const getReferrals = async (seniorId) => {
  const response = await apiClient.get(`${base(seniorId)}/referrals`);
  return response.data;
};

/** P3 EHR note comment thread for a specific action. */
export const getComments = async (seniorId, actionId) => {
  const response = await apiClient.get(
    `${base(seniorId)}/action-plan/actions/${encodeURIComponent(actionId)}/comments`,
  );
  return response.data;
};

/** P3 Add a comment to an EHR note thread. */
export const addComment = async (seniorId, actionId, body) => {
  const response = await apiClient.post(
    `${base(seniorId)}/action-plan/actions/${encodeURIComponent(actionId)}/comments`,
    withApiRole(body),
  );
  return response.data;
};

/**
 * Saved action-plan reviews for a senior, newest first (Previous Actions).
 * Contract: PHARMACY_DAP_BACKEND.md §7. Returns `{ items, nextCursor }`.
 */
export const getReviewHistory = async (seniorId, { tenant, cursor } = {}) => {
  const response = await apiClient.get(
    `${base(seniorId)}/action-plan/reviews`,
    {
      params: {
        ...(tenant ? { tenant } : {}),
        ...(cursor ? { cursor } : {}),
      },
    },
  );
  return response.data;
};

/** Fetch full patient profile (name, demographics, injuries, medications). */
export const getPatientProfile = async (seniorId) => {
  const response = await apiClient.get(
    `/v4/seniors/${encodeURIComponent(seniorId)}`,
  );
  return response.data?.data ?? response.data ?? null;
};
